import {CognitoUser, UserData} from 'amazon-cognito-identity-js'

export function getUserDataFresh(user: CognitoUser): Promise<UserData> {
  return new Promise((resolve, reject) => {
    user.getUserData(
      (err, data) => {
        if (err || !data) {
          reject(err ?? new Error('getUserData failed'))
          return
        }
        resolve(data)
      },
      {bypassCache: true}
    )
  })
}

/**
 * Whether SMS MFA is active for this user. Uses Cognito GetUser-style data so we still detect
 * SMS when PreferredMfaSetting is unset (common for console-enabled or legacy MFA) — Amplify's
 * getPreferredMFA rejects that case as "invalid MFA Type".
 */
export async function resolveSmsMfaEnabled(user: CognitoUser): Promise<boolean> {
  try {
    const data = await getUserDataFresh(user)
    const pref = data.PreferredMfaSetting
    if (pref === 'SMS' || pref === 'SMS_MFA') {
      return true
    }
    const list = data.UserMFASettingList
    if (Array.isArray(list) && list.includes('SMS_MFA')) {
      return true
    }
    if (Array.isArray(data.MFAOptions) && data.MFAOptions.length > 0) {
      return true
    }
    return false
  } catch {
    return false
  }
}

/**
 * Turns on SMS MFA for a user whose phone number is verified. SMS becomes the preferred method
 * only when no authenticator app is enabled; otherwise the app stays preferred so sign-in keeps
 * asking for the app code (Amplify's setPreferredMFA(user, 'SMS') would demote it).
 */
export async function enableCognitoSmsMfa(user: CognitoUser): Promise<void> {
  const data = await getUserDataFresh(user)
  const totpOn =
    Array.isArray(data.UserMFASettingList) && data.UserMFASettingList.includes('SOFTWARE_TOKEN_MFA')
  await new Promise<void>((resolve, reject) => {
    user.setUserMfaPreference({PreferredMfa: !totpOn, Enabled: true}, null, (err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

/**
 * Turns off SMS MFA via SetUserMFAPreference. Use this instead of Auth.setPreferredMFA(user,
 * 'NOMFA'), which throws Invalid MFA when PreferredMfaSetting is unset but SMS_MFA is in
 * UserMFASettingList (same Amplify _getMfaTypeFromUserData gap as getPreferredMFA).
 *
 * @returns true if Cognito was updated, false if SMS MFA was not enabled
 */
export async function disableCognitoSmsMfa(user: CognitoUser): Promise<boolean> {
  const data = await getUserDataFresh(user)
  const list = data.UserMFASettingList
  if (Array.isArray(list) && list.includes('SMS_MFA')) {
    await new Promise<void>((resolve, reject) => {
      user.setUserMfaPreference(
        {PreferredMfa: false, Enabled: false},
        null,
        (err) => {
          if (err) {
            reject(err)
            return
          }
          resolve()
        }
      )
    })
    return true
  }
  if (Array.isArray(data.MFAOptions) && data.MFAOptions.length > 0) {
    await new Promise<void>((resolve, reject) => {
      user.disableMFA((err, _result) => {
        if (err) {
          reject(err)
          return
        }
        resolve()
      })
    })
    return true
  }
  return false
}
